import { CameraIcon } from '@heroicons/react/24/solid'
import React, { FC, useRef, useState } from 'react'
import { SpinnerIcon, ImgWrapper } from '@sistemiv/s-components'
import { UserIcon } from '@heroicons/react/24/outline'
import { useUser } from '../../repositories/users/user.repository'
import { useUpdateUserPhoto } from '../../repositories/users/mutations/user-update-photo.mutation'
import { imageBase } from '../../services/http-common'
import { useTranslation } from 'react-i18next'
import { useResponsive } from '../context/ResponsiveContextProvider'
import { ProfileSettings } from './ProfileSettings'
import { useAuth } from '../../hooks/useAuth'
import { EMyAccountTab } from './MyAccount.utils'
import MyAccountOrganizationInfo from './MyAccountOrganizationInfo'

const MyAccount: FC = () => {
  const { isMobile } = useResponsive()
  const { userId } = useAuth()
  const { t } = useTranslation()
  const [activeTab, setActiveTab] = useState(EMyAccountTab.PROFILE)
  const photoInputRef = useRef<HTMLInputElement>(null)

  const { data: user, isLoading: userLoading, isFetching: fetchingUser } = useUser({ userId })
  const { mutate: updateUserPhoto, isPending: photoUpdating } = useUpdateUserPhoto()

  const handlePhotoUpdate = (e: any) => {
    const file = e.target.files.length > 0 ? e.target.files[0] : null
    if (!file || !userId) return
    updateUserPhoto({ userId, image: file })
  }

  return (
    <div className='h-full flex flex-col  relative'>
      <div className='flex-none px-8 py-6 bg-gradient-to-b from-[#E1E2EA] to-white ${}'>
        <div
          onClick={() => {
            if (photoInputRef.current) {
              photoInputRef.current.click()
            }
          }}
          className='group w-20 h-20 rounded-full border-2 border-sky-500 relative overflow-hidden flex items-center justify-center bg-[#7B87AE] text-white select-none cursor-pointer'
        >
          {!userId || userLoading ? (
            <SpinnerIcon className='w-6 h-6 text-sky-500' />
          ) : (
            <ImgWrapper
              src={`${imageBase}/${user?.data.id}/76?p=${fetchingUser ? '' : new Date().getMilliseconds()}`}
              className='w-full h-full'
              onErrorComponent={
                <p className='font-bold text-6xl'>
                  {user ? user.data?.firstName?.charAt(0) : <UserIcon className='w-full h-full text-white' />}
                </p>
              }
            />
          )}
          <span className='absolute z-10 flex justify-center items-center bottom-0 bg-black bg-opacity-30 group-hover:bg-opacity-50 w-full py-0.5'>
            <CameraIcon className='w-4 h-4' />
          </span>
          {photoUpdating && (
            <div className='absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2'>
              <SpinnerIcon className='text-white' />
            </div>
          )}
        </div>
        <input
          type='file'
          className='hidden'
          accept='image/png,image/jpg,image/jpeg'
          ref={photoInputRef}
          onChange={handlePhotoUpdate}
        />
      </div>
      <div className={`grow overflow-auto ${!isMobile && 'px-8 py-6'}`}>
        {isMobile ? (
          <div className='px-5 flex flex-col grow overflow-auto overflow-y-auto'>
            <div className='relative flex justify-start gap-x-8 text-slate-600 flex-none'>
              <div className='absolute bottom-0 w-full h-0.5 z-0 bg-gray-300 ' data-testid='tabs-panel' />
              <div
                key={`tab-${EMyAccountTab.PROFILE}`}
                onClick={() => setActiveTab(EMyAccountTab.PROFILE)}
                data-testid={`tab-${EMyAccountTab.PROFILE}`}
                className={`relative z-10 py-3 cursor-pointer border-b-2 whitespace-nowrap ${
                  activeTab === EMyAccountTab.PROFILE
                    ? 'border-blue-500 text-blue-500 hover:border-blue-500'
                    : 'border-gray-300 hover:border-black'
                }`}
              >
                {t('profile')}
              </div>
              <div
                key={`tab-${EMyAccountTab.ORGANIZATION}`}
                onClick={() => setActiveTab(EMyAccountTab.ORGANIZATION)}
                data-testid={`tab-${EMyAccountTab.ORGANIZATION}`}
                className={`relative z-10 py-3 cursor-pointer border-b-2 whitespace-nowrap ${
                  activeTab === EMyAccountTab.ORGANIZATION
                    ? 'border-blue-500 text-blue-500 hover:border-blue-500'
                    : 'border-gray-300 hover:border-black'
                }`}
              >
                {t('organization')}
              </div>
            </div>
            <div className='pb-2 grow'>
              {activeTab === EMyAccountTab.PROFILE ? (
                <div className='mt-4'>
                  <ProfileSettings showTitle={false} />
                </div>
              ) : activeTab === EMyAccountTab.ORGANIZATION ? (
                <div className='pt-8'>
                  <MyAccountOrganizationInfo />
                </div>
              ) : (
                <div></div>
              )}
            </div>
          </div>
        ) : (
          <div className={`grid md:grid-cols-2 gap-x-10 w-full xl:w-5/6`}>
            <ProfileSettings />
            <div className='space-y-5'>
              <MyAccountOrganizationInfo showTitle />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default MyAccount
